import { createSlice } from '@reduxjs/toolkit'
import { Collection } from 'constants/index'
import { caseInsensitiveSort, getVenueTitle } from 'utils'
import { FirestoreDatabase, storageRef } from '../index'
import { signOut } from 'store/auth'
import { sortBy } from 'lodash'

export const initialState = {
  error: null,
  inProgress: false,
  venues: [],
  venue: null,
  currentPage: 0,
  pageSize: 10
}

const venueSlice = createSlice({
  name: 'venue',
  initialState: initialState,
  reducers: {
    setError: (state, action) => {
      state.error = action.payload
    },
    setProgress: (state, action) => {
      state.inProgress = action.payload
    },
    setVenues: (state, action) => {
      state.venues = action.payload
    },
    setVenue: (state, action) => {
      state.venue = action.payload
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload
    }
  },
  extraReducers: {
    [signOut.fulfilled]: (state) => {
      state.error = null
      state.inProgress = false
      state.venues = []
      state.venue = null
      state.currentPage = 0
      state.pageSize = 10
    }
  }
})

export const addVenue = (payload) => async (dispatch, getState) => {
  try {
    const user = getState().auth.user
    dispatch(venueSlice.actions.setProgress(true))
    const doc = await FirestoreDatabase.collection(Collection.VENUES).add({
      ...payload,
      conferenceId: user?.conferenceId
    })
    return doc.id
  } catch (err) {
    dispatch(venueSlice.actions.setError(err))
  } finally {
    dispatch(venueSlice.actions.setProgress(false))
  }
}

export const fetchVenues = (conferenceId, callback) => {
  try {
    return FirestoreDatabase.collection(Collection.VENUES)
      .where('conferenceId', '==', conferenceId)
      .onSnapshot((documents) => {
        const venues = []
        documents.forEach((doc) => {
          venues.push({
            id: doc.id,
            ...doc.data(),
            venueText: getVenueTitle(doc.data()?.title, doc.data()?.floorNum, doc.data()?.roomNum)
          })
        })
        callback(caseInsensitiveSort(venues, 'title'))
      })
  } catch (err) {
    console.error(err)
  }
}

export const getVenuesInConference = (conferenceId) => async (dispatch) => {
  try {
    dispatch(venueSlice.actions.setProgress(true))
    const venuesSnap = await FirestoreDatabase.collection(Collection.VENUES)
      .where('conferenceId', '==', conferenceId)
      .get()
    const venues = venuesSnap.docs?.map((doc) => ({
      id: doc?.id,
      ...doc.data(),
      venueText: getVenueTitle(doc.data()?.title, doc.data()?.floorNum, doc.data()?.roomNum)
    }))
    const orderedVenues = sortBy(venues, (obj) => obj?.title?.toLowerCase())
    dispatch(venueSlice.actions.setVenues(orderedVenues))
    dispatch(venueSlice.actions.setProgress(false))
  } catch (err) {
    dispatch(venueSlice.actions.setError(err))
    dispatch(venueSlice.actions.setProgress(false))
  }
}

export const fetchVenueById = (venueId) => async (dispatch) => {
  try {
    dispatch(venueSlice.actions.setProgress(true))
    const snapshot = await FirestoreDatabase.collection(Collection.VENUES).doc(venueId).get()
    const e = snapshot.data()
    dispatch(
      venueSlice.actions.setVenue({
        id: snapshot.id,
        ...e
      })
    )
  } catch (err) {
    dispatch(venueSlice.actions.setError(err))
  } finally {
    dispatch(venueSlice.actions.setProgress(false))
  }
}

export const checkVenueExistsInEvents = (venueId) => async (dispatch, getState) => {
  try {
    dispatch(venueSlice.actions.setProgress(true))
    const user = getState().auth.user
    const snapshot = await FirestoreDatabase.collection(Collection.EVENTS)
      .where('conferenceId', '==', user?.conferenceId)
      .where('eventVenue', '==', venueId)
      .get()

    return !snapshot.empty
  } catch (err) {
    dispatch(venueSlice.actions.setError(err))
  } finally {
    dispatch(venueSlice.actions.setProgress(false))
  }
}

export const checkVenueExistsInTalks = (venueId) => async (dispatch, getState) => {
  try {
    dispatch(venueSlice.actions.setProgress(true))
    const user = getState().auth.user
    const snapshot = await FirestoreDatabase.collection(Collection.TALKS)
      .where('conferenceId', '==', user?.conferenceId)
      .where('venueId', '==', venueId)
      .get()

    return !snapshot.empty
  } catch (err) {
    dispatch(venueSlice.actions.setError(err))
  } finally {
    dispatch(venueSlice.actions.setProgress(false))
  }
}

export const updateVenueById = (venueId, payload) => async (dispatch) => {
  try {
    dispatch(venueSlice.actions.setProgress(true))
    await FirestoreDatabase.collection(Collection.VENUES).doc(venueId).update(payload)
    return true
  } catch (err) {
    dispatch(venueSlice.actions.setError(err))
  } finally {
    dispatch(venueSlice.actions.setProgress(false))
  }
}

export const deleteVenueById = (conferenceId, venueId) => async (dispatch) => {
  try {
    dispatch(venueSlice.actions.setProgress(true))
    const snapshot = await FirestoreDatabase.collection(Collection.VENUES).doc(venueId).get()
    const e = snapshot.data()
    try {
      await storageRef.child(`${conferenceId}/${Collection.VENUES}/${e.imageName}`).delete()
    } catch (e) {
      // do nothing
    }
    FirestoreDatabase.collection(Collection.VENUES).doc(venueId).delete()
  } catch (err) {
    dispatch(venueSlice.actions.setError(err))
  } finally {
    dispatch(venueSlice.actions.setProgress(false))
  }
}

export const bulkInsertVenues = (venues) => async (dispatch) => {
  try {
    dispatch(venueSlice.actions.setProgress(true))
    const collectionRef = FirestoreDatabase.collection(Collection.VENUES)
    const batch = FirestoreDatabase.batch()
    venues.forEach((docData) => {
      const docRef = collectionRef.doc()
      batch.set(docRef, docData)
    })
    await batch.commit()
  } catch (err) {
    dispatch(venueSlice.actions.setError(err))
  } finally {
    dispatch(venueSlice.actions.setProgress(false))
  }
}

export const resetVenue = () => (dispatch) => dispatch(venueSlice.actions.setVenue(null))

export default venueSlice
